import * as React from 'react';

import { formatMoney, GroupRow, Row } from '@rematter/ui-web';

import { useInventoryUnit } from '../../hooks/useInventoryUnit';
import { useTranslations } from '../../i18n/useTranslations';
import { InventoryLevelRecord } from '../../types/inventoryLevel';

import { getLocale } from '~/shared/utils/locale';

type Props = GroupRow<InventoryLevelRecord>;

export const InventoryLevelGroupCell: React.FC<Props> = ({ children }) => {
  const t = useTranslations();
  const { formatQuantityValue } = useInventoryUnit();

  const groupName =
    children[0]?.itemGroupName ||
    children[0]?.itemGroupNumber ||
    t.inventoryLevelsView.groups.ungrouped;

  const itemCount = children.length;

  const totalQuantity = children.reduce((sum, row) => sum + row.quantity, 0);
  const quantityUnit = children[0]?.quantityUnit;

  const currencyTotals = React.useMemo(() => {
    const totalsMap = new Map<string, number>();
    for (const row of children) {
      for (const cv of row.currencyValues) {
        if (cv.currencyCode === 'NEUTRAL' || cv.totalValue == null) continue;
        totalsMap.set(
          cv.currencyCode,
          (totalsMap.get(cv.currencyCode) ?? 0) + cv.totalValue,
        );
      }
    }
    const locale = getLocale();
    return Array.from(totalsMap.entries()).map(([code, total]) =>
      formatMoney(total, code, locale),
    );
  }, [children]);

  return (
    <Row gap3 className="@InventoryLevelGroupCell font-medium">
      <span>{groupName}</span>
      <Row gap1 className="text-sm font-normal text-neutral/50">
        &bull;
        <span>{t.inventoryLevelsView.groups.items(itemCount)}</span>
        &bull;
        <span>
          {quantityUnit
            ? formatQuantityValue(totalQuantity, quantityUnit)
            : totalQuantity.toLocaleString()}
        </span>
        {currencyTotals.map((formatted) => (
          <React.Fragment key={formatted}>
            &bull;
            <span>{formatted}</span>
          </React.Fragment>
        ))}
      </Row>
    </Row>
  );
};
